import classNames from 'classnames';
import { FC } from 'react';

import { EntitlementViewBase, ProductViewBase } from '@amzn/coral_com-amazon-sssapinterface-model-structures-credits';
import { KatProgress } from '@amzn/katal-react';
import { useBundle } from '@amzn/react-arb-tools';
import {
  FlatCard,
  formatTimestampToLongDate,
  SECTION_THEME,
  SignatoryBadge,
  SIGNATORY_BADGE_THEME,
  Tagline,
  TAGLINE_COLORS,
} from '@amzn/sss-website-theme';

import { isUkLaunchEnabled } from 'src/clients/weblab/weblab-client';
import { CreditsComputedInfo } from 'src/components/pages/checkout/credits-computed-info/CreditsComputedInfo';
import { CHECKOUT_BUNDLE_NAME, COMMON_BUNDLE_NAME } from 'src/constants';
import { getCurrencySymbol, shouldShowOriginalPrice, formatProductPrice, t } from 'src/helpers';
import { CREDIT_SEGMENT_TAG_CONFIG, useProductTags } from 'src/hooks';
import fallbackImage1 from 'src/images/fallback-image-1.jpg';

import styles from './CheckoutStep2.module.scss';

interface CheckoutStep2Props {
  product: ProductViewBase | null | undefined;
  entitlement: EntitlementViewBase | null | undefined;
}

export const CheckoutStep2: FC<CheckoutStep2Props> = ({ product, entitlement }) => {
  const [commonBundle] = useBundle(COMMON_BUNDLE_NAME);
  const [checkoutBundle] = useBundle(CHECKOUT_BUNDLE_NAME);
  const tags = useProductTags(product);

  if (!product || !entitlement) {
    return (
      <div role="status">
        <KatProgress katAriaLabel={t(commonBundle, 'loading_aria_label')} indeterminate type="circular" />
      </div>
    );
  }

  const currencySymbol = getCurrencySymbol(entitlement.entitlementCurrencyCode ?? product?.productOfferCurrency);
  const orderNeedByDateString = formatTimestampToLongDate(entitlement.entitlementNeedByDate);
  const showOriginalPrice = shouldShowOriginalPrice(product);

  return (
    <section className={styles.checkoutStep2}>
      <FlatCard
        className={classNames(styles.card, styles.firstCard)}
        // TODO: Replace with actual image from data from the backend (https://issues.amazon.com/issues/ASX-983)
        image={{ src: fallbackImage1, alt: 'fallback image alt' }}
        variant={SECTION_THEME.Teal}
        title={t(checkoutBundle, 'checkout_step_2_first_card_title')}
        rows={[
          {
            label: t(checkoutBundle, 'checkout_first_card_name_title'),
            value: product.productTitle,
          },
          {
            label: t(checkoutBundle, 'checkout_first_card_segment_title'),
            value: t(commonBundle, CREDIT_SEGMENT_TAG_CONFIG[product.productCreditSegment].stringId),
          },
        ]}
      >
        <div className={styles.content}>
          <div className={styles.carbonCreditType}>
            <p className={styles.title}>{t(checkoutBundle, 'checkout_first_card_type_title')}</p>
            <ul className={styles.taglineList}>
              {tags.map((tag, i) => (
                <li key={`tag-${i}-${tag.copy}`}>
                  <Tagline className={styles.tagline} icon={tag.icon} color={TAGLINE_COLORS.TRANSPARENT}>
                    {tag.copy}
                  </Tagline>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.contractPrice}>
            <p className={styles.title}>{t(checkoutBundle, 'checkout_first_card_price_title')}</p>
            <div className={styles.priceRow}>
              {showOriginalPrice && (
                <p className={classNames(styles.body, styles.originalPrice)} data-testid="checkout-original-price">
                  {currencySymbol}
                  {formatProductPrice(product.productBaseOfferPrice)}
                </p>
              )}
              <p className={styles.body}>
                {t(
                  checkoutBundle,
                  isUkLaunchEnabled ? 'checkout_first_card_per_credit' : 'checkout_first_card_price_label',
                  {
                    currencySymbol,
                    price: formatProductPrice(product.productMatchedOfferPrice),
                  }
                )}
              </p>
              {showOriginalPrice && (
                <SignatoryBadge
                  className={styles.signatoryBadge}
                  theme={SIGNATORY_BADGE_THEME.Light}
                  data-testid="checkout-signatory-badge"
                >
                  {t(checkoutBundle, 'checkout_step_1_signatory_price_tag')}
                </SignatoryBadge>
              )}
            </div>
          </div>
        </div>
      </FlatCard>
      <FlatCard
        className={classNames(styles.card, styles.secondCard)}
        title={t(checkoutBundle, 'checkout_sign_agreements_title')}
      >
        <div className={styles.dashboardItem}>
          <p className={styles.disclaimerTitle}>{t(checkoutBundle, 'checkout_step_2_disclaimer_title')}</p>
          <p className={styles.disclaimerBody}>{t(checkoutBundle, 'checkout_step_2_disclaimer_description')}</p>
        </div>
        <div className={styles.orderDetails}>
          <p className={styles.orderDetailsTitle}>{t(checkoutBundle, 'checkout_you_order_details')}</p>
          <Tagline
            color={TAGLINE_COLORS.GRAY}
          >{`${t(checkoutBundle, 'checkout_request_id')}${entitlement.entitlementId}`}</Tagline>
        </div>
        {/* TODO: Get TCP tier/base pricing from entitlement once backend changes are in — https://issues.amazon.com/ASX-1974 */}
        <CreditsComputedInfo
          product={product}
          entitlement={entitlement}
          quantity={entitlement.entitlementQuantity}
          deliveryDate={orderNeedByDateString}
          marketplaceId={entitlement.obfuscatedMarketplaceId}
          showBundleNote={false}
        />
      </FlatCard>
    </section>
  );
};
